import React, { useState, useMemo, useEffect } from 'react';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { Search, MapPin, BookOpen, DollarSign, Clock, FileText, Calendar, MessageSquare, SlidersHorizontal, AlertCircle, CheckCircle } from 'lucide-react';
import { buildPageSEO, breadcrumbSchema } from '@/utils/seo';
import { useSearchParams } from 'react-router-dom';
import Modal from '../components/Modal/Modal';
import { CaseListCard } from '../components/common/CaseListCard';
import SearchableDropdown from '../components/common/SearchableDropdown';
import SearchableInput from '../components/common/SearchableInput';
import DualRangeSlider from '../components/common/DualRangeSlider';
import WeeklyTimeTable from '../components/common/WeeklyTimeTable';
import { Pagination } from '@tutor/ui';
import { CaseCardSkeletonGrid } from '../components/skeletons';
import { MALAYSIAN_STATES, EDUCATION_LEVELS } from '../constants/dropdownOptions';
import { formatEducationSystemLevel, getSubjectsForLevel } from '@tutor/types';
import { useCases } from '../hooks/api/useCases';
import { useCaseDetail } from '../hooks/api/useCaseDetail';
import { useApplication } from '../hooks/api/useApplication';
import { useTutorCompletionStatus } from '../hooks/api/useTutorCompletionStatus';
import { useAuthContext } from '../contexts/useAuthContext';
import type { CaseData, GetCasesParams } from '../types/api';
import { useI18n } from '@/i18n';
import { localizeStringList } from '@/i18n/options';
import { getWeeklyTimeTableI18nProps } from '@/utils/weeklyTimeTableI18n';

const normalizePreferredTimes = (
  preferredTimes: CaseData['preferredTimes']
): Record<string, string[]> =>
  Object.fromEntries(
    Object.entries(preferredTimes ?? {}).filter(
      ([day, slots]) =>
        day.trim().length > 0 &&
        Array.isArray(slots) &&
        slots.every((slot) => typeof slot === 'string' && slot.trim().length > 0)
    )
  );

const AllCasesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { locale } = useI18n();
  const weeklyTimeTableI18n = getWeeklyTimeTableI18nProps(locale);
  const [selectedCase, setSelectedCase] = useState<CaseData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [applicationMessage, setApplicationMessage] = useState('');
  const { firebaseUser, profile } = useAuthContext();
  const isZh = locale === 'zh-CN';

  // Application mutation
  const applicationMutation = useApplication();

  // Fetch tutor completion status if user is a tutor
  const { data: completionStatus } = useTutorCompletionStatus(firebaseUser?.uid, profile?.role === 'tutor');

  // Auto close modal after successful application
  useEffect(() => {
    if (applicationMutation.isSuccess) {
      const timer = setTimeout(() => {
        setSelectedCase(null);
        setApplicationMessage('');
      }, 1000); // Close after 1 second
      return () => clearTimeout(timer);
    }
  }, [applicationMutation.isSuccess]);

  const [filters, setFilters] = useState({
    location: '',
    level: '',
    subject: '',
  });
  const [advancedFilters, setAdvancedFilters] = useState({
    priceMin: '',
    priceMax: '',
  });
  const [tempAdvancedFilters, setTempAdvancedFilters] = useState({
    priceMin: '',
    priceMax: '',
  });
  const [showAdvanced, setShowAdvanced] = useState(false);

  const updateFilters = (nextFilters: typeof filters) => {
    setFilters(nextFilters);
    setCurrentPage(1);
  };

  // Fetch case by ID from query param (e.g. navigated from landing page)
  const caseIdParam = searchParams.get('caseId') || undefined;
  const { data: linkedCaseData } = useCaseDetail(caseIdParam, 'recommendation');

  // Auto-open modal when navigated with ?caseId=
  useEffect(() => {
    if (linkedCaseData?.case) {
      setSelectedCase(linkedCaseData.case);
    }
  }, [linkedCaseData]);

  // Initialize filters from URL query parameters
  useEffect(() => {
    const location = searchParams.get('location') || '';
    const level = searchParams.get('level') || '';
    const subject = searchParams.get('subject') || '';
    const priceMin = searchParams.get('priceMin') || '';
    const priceMax = searchParams.get('priceMax') || '';

    setFilters({ location, level, subject });
    setAdvancedFilters({ priceMin, priceMax });
    setCurrentPage(1);
  }, [searchParams]);

  // Build query parameters
  const queryParams: GetCasesParams = useMemo(
    () => ({
      page: currentPage,
      limit: 9,
      ...(filters.location && { state: filters.location.toLowerCase().replace(/\s+/g, '_') }),
      ...(filters.level && { level: filters.level as GetCasesParams['level'] }),
      ...(filters.subject && { subject: filters.subject }),
      ...(advancedFilters.priceMin && { minPrice: parseInt(advancedFilters.priceMin) }),
      ...(advancedFilters.priceMax && { maxPrice: parseInt(advancedFilters.priceMax) }),
    }),
    [currentPage, filters, advancedFilters]
  );

  // Use React Query Hook to fetch cases
  const { data, isLoading, error } = useCases(queryParams);

  // Filter out cases that the user has already applied to (render-time filtering)
  const cases = useMemo(
    () => (data?.cases ?? []).filter(caseItem => !caseItem.hasApplied),
    [data?.cases]
  );
  const totalPages = data?.pagination.totalPages ?? 1;

  // Get the filtered subjects based on the selected level
  const filteredSubjects = React.useMemo(() => {
    if (!filters.level) return [];
    return localizeStringList(locale, getSubjectsForLevel(filters.level));
  }, [filters.level, locale]);

  const preferredTimes = selectedCase ? normalizePreferredTimes(selectedCase.preferredTimes) : {};
  const hasPreferredTimes = Object.keys(preferredTimes).length > 0;
  const isTutorViewingOwnCase =
    profile?.role === 'tutor' &&
    !!selectedCase?.parentProfile?.userId &&
    selectedCase.parentProfile.userId === profile.userId;

  // Handle search/filter button click
  const handleSearch = () => {
    setCurrentPage(1);
  };

  const seo = buildPageSEO(
    isZh ? '浏览马来西亚补习个案' : 'Browse Tuition Cases in Malaysia',
    isZh
      ? '查看马来西亚各地家长发布的开放补习个案，并寻找符合你专长的教学机会。'
      : 'View open tuition cases posted by parents across Malaysia. Apply as a tutor or find a case that matches your expertise.',
    '/cases',
  );

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.canonical} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.canonical} />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Cases', path: '/cases' },
          ]))}
        </script>
      </Helmet>
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-secondary text-white py-20 flex items-center justify-center min-h-[26vh]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{isZh ? '浏览开放个案' : 'Browse Available Cases'}</h1>
        </div>
      </section>

      {/* Search Section - Overlapping */}
      <section className="relative -mt-10 mb-20 md:mb-24 z-sticky">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Search Form Card */}
            <div className="bg-white rounded-2xl shadow-xl overflow-visible">

              {/* Search Form */}
              <div className="p-4 md:p-6 lg:p-8">
                <div className="space-y-3 md:space-y-0 md:flex md:flex-wrap md:gap-3 lg:flex lg:flex-nowrap lg:gap-4 lg:items-end">
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="inline h-4 w-4 mr-1" />
                      {isZh ? '地点' : 'Location'}
                    </label>
                    <SearchableInput
                      options={MALAYSIAN_STATES}
                      value={filters.location}
                      onChange={(value) => updateFilters({ ...filters, location: value })}
                      placeholder={isZh ? '选择地点' : 'Select location'}
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <BookOpen className="inline h-4 w-4 mr-1" />
                      {isZh ? '程度' : 'Level'}
                    </label>
                    <SearchableDropdown
                      options={EDUCATION_LEVELS}
                      value={filters.level}
                      onChange={(value) => updateFilters({ ...filters, level: String(value), subject: '' })}
                      placeholder={isZh ? '选择程度' : 'Select level'}
                      searchable={true}
                      truncate={true}
                    />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <BookOpen className="inline h-4 w-4 mr-1" />
                      {isZh ? '科目' : 'Subject'}
                    </label>
                    <SearchableDropdown
                      options={filteredSubjects}
                      value={filters.subject}
                      onChange={(value) => updateFilters({ ...filters, subject: String(value) })}
                      placeholder={filters.level ? (isZh ? '选择科目' : 'Select subject') : (isZh ? '请先选择程度' : 'Select a level first')}
                      searchable={true}
                      disabled={!filters.level}
                    />
                  </div>

                  {/* Buttons Container */}
                  <div className="flex gap-2 md:mt-6 lg:mt-0 lg:shrink-0">
                    <button onClick={() => {
                      setTempAdvancedFilters(advancedFilters);
                      setShowAdvanced(true);
                    }} className="flex-1 md:flex-none bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold px-4 md:px-3 py-2.5 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center whitespace-nowrap" title={isZh ? '筛选' : 'Filters'}>
                      <SlidersHorizontal className="h-5 w-5" />
                    </button>
                    <button onClick={handleSearch} disabled={isLoading} className="flex-1 md:flex-none bg-primary hover:bg-primary/90 text-white font-semibold px-4 md:px-3 py-2.5 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed" title={isZh ? '搜索' : 'Search'}>
                      <Search className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Filters Modal */}
      <Modal
        isOpen={showAdvanced}
        onClose={() => {
          setTempAdvancedFilters(advancedFilters);
          setShowAdvanced(false);
        }}
        title={isZh ? '进阶筛选' : 'Advanced Filters'}
        maxWidth="max-w-2xl"
        footer={
          <div className="flex gap-4">
            <button
              onClick={() => {
                setTempAdvancedFilters({ priceMin: '0', priceMax: '200' });
              }}
              className="flex-1 px-6 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-all duration-200"
            >
              {isZh ? '重设' : 'Reset'}
            </button>
            <button
              onClick={() => {
                setAdvancedFilters(tempAdvancedFilters);
                setCurrentPage(1);
                setShowAdvanced(false);
              }}
              className="flex-1 px-6 py-2 bg-primary hover:bg-primary/90 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {isZh ? '应用筛选' : 'Apply Filters'}
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <DualRangeSlider
              min={0}
              max={200}
              minValue={tempAdvancedFilters.priceMin ? parseInt(tempAdvancedFilters.priceMin) : 0}
              maxValue={tempAdvancedFilters.priceMax ? parseInt(tempAdvancedFilters.priceMax) : 200}
              onChange={(minValue, maxValue) => setTempAdvancedFilters({ ...tempAdvancedFilters, priceMin: String(minValue), priceMax: String(maxValue) })}
              label={
                <span className="flex items-center">
                  <DollarSign className="h-4 w-4 mr-1" />
                  {isZh ? '价格范围 (RM/小时)' : 'Price Range (RM/hour)'}
                </span>
              }
            />
          </div>
        </div>
      </Modal>

      <div className="container mx-auto px-4 py-8">
        {error && (
          <div className="mb-4 flex items-center text-red-600">
            <AlertCircle className="mr-2 h-4 w-4" />
            {error.message || (isZh ? '发生错误' : 'An error occurred')}
          </div>
        )}

        {/* Class Cards */}
        {isLoading ? (
          <CaseCardSkeletonGrid count={9} />
        ) : cases.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">{isZh ? '找不到符合条件的个案，请尝试调整筛选条件。' : 'No cases found matching your criteria. Try adjusting your filters.'}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {cases.map((caseItem) => (
                <CaseListCard
                  key={caseItem.id}
                  caseData={caseItem}
                  onViewDetails={(id) => {
                    applicationMutation.reset();
                    setSelectedCase(cases.find((c) => c.id === id) ?? null);
                    setApplicationMessage('');
                  }}
                  onShare={(id) => {
                    const url = `${window.location.origin}/cases?caseId=${id}`;
                    navigator.clipboard.writeText(url);
                    toast.success(isZh ? '链接已复制到剪贴板！' : 'Link copied to clipboard!');
                  }}
                />
              ))}
            </div>

            {/* Pagination */}
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              isLoading={isLoading}
            />
          </>
        )}
      </div>

      {/* Class Detail Modal */}
      {selectedCase && (
        <Modal
          isOpen={!!selectedCase}
          onClose={() => {
            applicationMutation.reset();
            setSelectedCase(null);
            setApplicationMessage('');
          }}
          title={
            <>
              Class Details <span className="text-sm text-gray-300 font-normal">({selectedCase.id} · Posted {new Date(selectedCase.createdAt).toLocaleDateString()})</span>
            </>
          }
          maxWidth="max-w-3xl"
          footer={
            !firebaseUser ? (
              <div className="text-center text-gray-600">
                <p className="text-sm">Please log in to apply for this case.</p>
              </div>
            ) : profile?.role !== 'tutor' ? (
              <div className="text-center text-gray-600">
                <p className="text-sm">Only tutors can apply for cases.</p>
              </div>
            ) : isTutorViewingOwnCase ? (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-600">
                  <AlertCircle className="h-5 w-5" />
                  <div className="text-left">
                    <p className="text-sm font-medium">You cannot apply to your own case.</p>
                  </div>
                </div>
              </div>
            ) : !completionStatus?.canApply ? (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2 text-amber-600 bg-amber-50 p-3 rounded-lg">
                  <AlertCircle className="h-5 w-5" />
                  <div className="text-left">
                    <p className="text-sm font-medium">Cannot apply yet</p>
                    <p className="text-xs text-amber-600">
                      Complete your Tutor profile before applying for cases.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {applicationMutation.isPending && (
                  <div className="text-center">
                    <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-white"></div>
                    <p className="text-sm text-gray-600 mt-2">Submitting application...</p>
                  </div>
                )}
                {applicationMutation.isSuccess && (
                  <div className="flex items-center justify-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
                    <CheckCircle className="h-5 w-5" />
                    <span>Application submitted successfully!</span>
                  </div>
                )}
                {applicationMutation.isError && (
                  <div className="flex items-center justify-center gap-2 text-red-600 bg-red-50 p-3 rounded-lg">
                    <AlertCircle className="h-5 w-5" />
                    <span>{(applicationMutation.error as Error)?.message || 'Failed to submit application'}</span>
                  </div>
                )}
                <button
                  onClick={() => {
                    if (selectedCase && !isTutorViewingOwnCase) {
                      applicationMutation.mutate({ caseId: selectedCase.id, message: applicationMessage || undefined });
                    }
                  }}
                  disabled={isTutorViewingOwnCase || applicationMutation.isPending || applicationMutation.isSuccess}
                  className="w-full btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {applicationMutation.isPending ? 'Submitting...' : applicationMutation.isSuccess ? 'Applied Successfully' : 'Apply for This Case'}
                </button>
              </div>
            )
          }
        >
          <div className="space-y-6">
            {/* Title with Price */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <h2 className="text-2xl font-bold">
                    {formatEducationSystemLevel(selectedCase.educationSystem, selectedCase.level, { useShortSystemLabel: true })} — {selectedCase.subject}
                  </h2>
                </div>
                <div className="text-primary font-bold text-2xl">
                  RM {selectedCase.hourlyRate?.toString() || '0'}
                  <span className="text-sm text-gray-600 font-normal"> / hour</span>
                </div>
              </div>

              {/* Subject Tabs */}
              <div className="flex flex-wrap gap-2">
                <span className="px-4 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  {selectedCase.subject}
                </span>
              </div>
            </div>

            {/* Case Information */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-base font-bold text-gray-800 mb-2 flex items-center">
                    <MapPin className="h-4 w-4 mr-2" />
                    Location
                  </h4>
                  <p className="text-gray-600 pl-6">{selectedCase.district ? `${selectedCase.district}, ` : ''}{selectedCase.state}</p>
                </div>
                <div>
                  <h4 className="text-base font-bold text-gray-800 mb-2 flex items-center">
                    <FileText className="h-4 w-4 mr-2" />
                    Tuition Type
                  </h4>
                  <p className="text-gray-600 pl-6">{selectedCase.tuitionType === 'physical' ? 'Physical' : 'Online'}</p>
                </div>
              </div>
            </div>

            {/* Schedule Section */}
            <div className="border-t pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-base font-bold text-gray-800 mb-2 flex items-center">
                    <Calendar className="h-4 w-4 mr-2" />
                    Frequency
                  </h4>
                  <p className="text-gray-600 pl-6">{selectedCase.lessonsPerWeek}x per week, {selectedCase.lessonDuration} hours each</p>
                </div>
                <div>
                  <h4 className="text-base font-bold text-gray-800 mb-2 flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    {locale === 'zh-CN' ? localizeStringList(locale, ['Languages'])[0] : 'Languages'}
                  </h4>
                  <p className="text-gray-600 pl-6">{localizeStringList(locale, selectedCase.teachingLanguages).join(', ')}</p>
                </div>
              </div>
              <div className="mt-4">
                <h4 className="text-base font-bold text-gray-800 mb-2 flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  {locale === 'zh-CN' ? '可上课时间' : 'Preferred Times'}
                </h4>
                {hasPreferredTimes ? (
                  <div className="ml-6 overflow-hidden rounded-lg border border-gray-200 bg-white">
                    <WeeklyTimeTable
                      selectedTimes={preferredTimes}
                      onChange={() => {}}
                      isEditable={false}
                      colorTheme="secondary"
                      {...weeklyTimeTableI18n}
                    />
                  </div>
                ) : (
                  <p className="pl-6 text-gray-600">{locale === 'zh-CN' ? '尚未设置可上课时间' : 'No preferred times set'}</p>
                )}
              </div>
              {/* Remarks */}
              {selectedCase.tutorRemarks && (
                <div className="mt-4">
                  <h4 className="text-base font-bold text-gray-800 mb-2 flex items-center">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Remarks
                  </h4>
                  <p className="text-gray-600 bg-gray-50 p-4 rounded-lg ml-6">{selectedCase.tutorRemarks}</p>
                </div>
              )}
            </div>

            {/* Application Message Section */}
            {firebaseUser && (
              <div className="border-t pt-4">
                <h4 className="text-base font-bold text-gray-800 mb-3 flex items-center">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Application Message (Optional)
                </h4>
                <textarea
                  value={applicationMessage}
                  onChange={(e) => setApplicationMessage(e.target.value)}
                  placeholder="Tell the parent why you're interested in this case and any relevant experience..."
                  maxLength={1000}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  rows={4}
                />
                <p className="text-xs text-gray-500 mt-1">{applicationMessage.length}/1000 characters</p>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
    </>
  );
};

export default AllCasesPage;
